import { X } from 'lucide-react';
import { useId, useState, type KeyboardEvent, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_TERM_LENGTH, MAX_TERMS } from './api';

function TermChips({
  terms,
  onChange,
}: {
  readonly terms: readonly string[];
  readonly onChange: (terms: readonly string[]) => void;
}) {
  const { t } = useTranslation('tools');
  return (
    <ul className="contents">
      {terms.map((term) => (
        <li
          key={term}
          className="inline-flex items-center gap-1 rounded-full border border-chip-border bg-muted py-0.5 pr-1 pl-2.5 text-sm"
        >
          {term}
          <button
            type="button"
            aria-label={t('cleanText.options.remove', { term })}
            onClick={() => onChange(terms.filter((x) => x !== term))}
            className="flex size-6 items-center justify-center rounded-full hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            <X aria-hidden="true" className="size-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * A field of chips: Enter adds the term, the × removes it. At most 50 terms of 1–100 characters
 * (the contract); the term typed is kept when it cannot be added, with the reason under the field.
 */
export function TermsInput({
  label,
  hint,
  placeholder,
  terms,
  onChange,
  inputRef,
}: {
  readonly label: string;
  readonly hint: string;
  readonly placeholder: string;
  readonly terms: readonly string[];
  readonly onChange: (terms: readonly string[]) => void;
  readonly inputRef?: Ref<HTMLInputElement>;
}) {
  const { t } = useTranslation('tools');
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<'tooLong' | 'tooMany' | null>(null);
  const id = useId();

  const add = (): void => {
    const term = draft.trim();
    if (term === '') return;
    if (term.length > MAX_TERM_LENGTH) return setProblem('tooLong');
    if (terms.length >= MAX_TERMS) return setProblem('tooMany');
    setProblem(null);
    setDraft('');
    if (!terms.includes(term)) onChange([...terms, term]);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      // Enter belongs to the field, not to a form around it.
      event.preventDefault();
      add();
    } else if (event.key === 'Backspace' && draft === '' && terms.length > 0) {
      onChange(terms.slice(0, -1));
    }
  };

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-sm font-semibold">
        {label}
      </label>
      <div className="flex min-h-11 flex-wrap items-center gap-1.5 rounded-md border border-[var(--control-border)] bg-card px-2 py-1.5 focus-within:ring-2 focus-within:ring-ring">
        <TermChips terms={terms} onChange={onChange} />
        <input
          id={id}
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="min-w-40 flex-1 bg-transparent py-1 text-sm outline-none"
        />
      </div>
      {problem && (
        <p role="alert" className="text-sm text-destructive">
          {t(`cleanText.options.${problem}`, {
            max: problem === 'tooLong' ? MAX_TERM_LENGTH : MAX_TERMS,
          })}
        </p>
      )}
      <p className="text-sm text-muted-foreground">{hint}</p>
    </div>
  );
}
